import { Triggers } from "processEditor/model/ITriggerData";
import { ITriggerValidatorProps } from "./validateConditionalTriggers";
import { ValidationError, ValidationErrorType } from "processEditor/model/ValidationData";

type SchemaKey = "inputSchema" | "outputSchema";

interface NormalizedTrigger {
    id: string | undefined;
    trimmedName: string;
    toolName: string;
    inputSchema: string;
    outputSchema?: string;
}

const getOptionalSchema = (value: unknown): string | undefined =>
    typeof value === "string" ? value : undefined;

const schemaValidators: Record<SchemaKey, (raw: string) => boolean> = {
    inputSchema: (raw) => {
        const parsed = JSON.parse(raw);
        return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed);
    },
    outputSchema: (raw) => {
        JSON.parse(raw);
        return true;
    }
};

const schemaErrorTypes: Record<SchemaKey, ValidationErrorType> = {
    inputSchema: ValidationErrorType.McpTriggerInvalidInputSchema,
    outputSchema: ValidationErrorType.McpTriggerInvalidOutputSchema
};

const collectMcpTriggers = ({ triggers }: ITriggerValidatorProps): NormalizedTrigger[] =>
    triggers
        .filter((trigger) => trigger?.selectedTypeId === Triggers.McpTrigger.triggerType)
        .map<NormalizedTrigger>((trigger) => {
            const toolName = typeof trigger?.parameters?.toolName === "string"
                ? trigger.parameters.toolName
                : "";
            const inputSchema = typeof trigger?.parameters?.inputSchema === "string"
                ? trigger.parameters.inputSchema
                : "";

            return {
                id: trigger?.id,
                toolName,
                trimmedName: toolName.trim(),
                inputSchema,
                outputSchema: getOptionalSchema(trigger?.parameters?.outputSchema)
            };
        });

const validateSchema = (trigger: NormalizedTrigger, key: SchemaKey, errors: ValidationError[]) => {
    const raw = trigger[key];
    if (typeof raw !== "string" || raw.trim().length === 0) {
        return;
    }

    try {
        const isValid = schemaValidators[key](raw);
        if (!isValid) {
            throw new Error(`Schema "${key}" must resolve to an object`);
        }
    } catch {
        errors.push(new ValidationError(schemaErrorTypes[key], trigger.id, key));
    }
};

const reportDuplicateNames = (groups: Map<string, Array<string | undefined>>, errors: ValidationError[]) => {
    groups.forEach((ids) => {
        if (ids.length > 1) {
            ids.forEach((id) => {
                errors.push(new ValidationError(ValidationErrorType.McpTriggerDuplicateToolName, id, "toolName"));
            });
        }
    });
};

const validateTrigger = (trigger: NormalizedTrigger, errors: ValidationError[], nameBuckets: Map<string, Array<string | undefined>>) => {
    if (!trigger.trimmedName) {
        errors.push(new ValidationError(ValidationErrorType.McpTriggerMissingToolName, trigger.id, "toolName"));
        return;
    }

    const existingIds = nameBuckets.get(trigger.trimmedName) ?? [];
    existingIds.push(trigger.id);
    nameBuckets.set(trigger.trimmedName, existingIds);

    const inputSchema = trigger.inputSchema?.trim();
    if (!inputSchema) {
        errors.push(new ValidationError(ValidationErrorType.McpTriggerInvalidInputSchema, trigger.id, "inputSchema"));
    } else {
        validateSchema(trigger, "inputSchema", errors);
    }

    validateSchema(trigger, "outputSchema", errors);
};

// ensures that toolName is present, unique, and schemas contain valid JSON
export default function ({ triggers, validationErrors }: ITriggerValidatorProps) {
    const mcpTriggers = collectMcpTriggers({ triggers, validationErrors });

    if (mcpTriggers.length === 0) {
        return;
    }

    const nameBuckets = new Map<string, Array<string | undefined>>();

    mcpTriggers.forEach((trigger) => validateTrigger(trigger, validationErrors, nameBuckets));

    reportDuplicateNames(nameBuckets, validationErrors);
}
